// Kiểu dữ liệu + hàm thuần cho danh sách vật tư của một đơn hàng.
//
// Tách khỏi PackagingItemsEditor để phá vòng lặp import: hook đồng bộ
// (use-order-packaging-sync) cần các hàm này, còn editor lại cần hook. Trước
// đây hai file import chéo nhau và chỉ không nổ nhờ các hàm đều là function
// declaration được hoisting — đổi một hàm sang const arrow là vỡ lúc chạy.

import type { PickedPackagingMaterial } from '@/components/packaging/packaging-material-picker'
import { newMaterialRowId } from '@/lib/material-row-id'

export type PackagingItemSource = 'bom_autofill' | 'manual'

export interface PackagingDraftItem {
    packaging_material_id: string
    material_name: string
    material_sku: string
    material_unit: string
    quantity: number
    available: number
    source: PackagingItemSource
    /** Đơn giá vật tư (giá vật tư). 0 = không tính tiền. */
    unit_price: number
    /** Chiết khấu dòng (%) — người bán nhập; vật tư chịu CK như dòng hàng. */
    discount_percent: number
    /** Sản phẩm mà dòng vật tư thuộc về (để hiển thị inline). '' nếu không. */
    product_id: string
    /** Khoá DÒNG ĐƠN của dòng vật tư — create: field.id, edit: order_item.id.
     *  Cho phép trùng sản phẩm trên nhiều dòng, mỗi dòng vật tư/CK riêng. '' nếu
     *  không gắn dòng (đính-kèm/thêm tay). */
    line_key: string
    /** Định danh DÒNG của vật tư thêm tay (line_key rỗng). Bắt buộc vì cùng một
     *  vật tư có thể xuất hiện nhiều dòng, nên (material_id, line_key) không còn
     *  phân biệt được. Dòng do BOM/surcharge sinh không cần — chúng đã duy nhất
     *  theo (line_key, material_id). */
    row_id?: string
    /** Số thứ tự dòng trên bảng hàng hóa (dãy số CHUNG với dòng sản phẩm) — cho
     *  phép xen kẽ sản phẩm và vật tư. Chỉ có nghĩa với vật tư THÊM TAY
     *  (`line_key` rỗng); vật tư định mức mang số của dòng sản phẩm cha. Rỗng =
     *  dòng chưa được đánh số (đơn cũ) → xếp sau toàn bộ sản phẩm như trước.
     *  Xem `@/lib/order-line-order`. */
    row_seq?: number
}

/**
 * Cộng dồn `quantity_available` của tất cả lô theo material_id.
 * Map rỗng nếu lotsData chưa load.
 */
export function buildAvailableByMaterial(
    lots:
        | ReadonlyArray<{
              packaging_material_id: string
              quantity_available?: number
          }>
        | undefined,
): Map<string, number> {
    const map = new Map<string, number>()
    for (const lot of lots ?? []) {
        const cur = map.get(lot.packaging_material_id) ?? 0
        map.set(lot.packaging_material_id, cur + (lot.quantity_available ?? 0))
    }
    return map
}

/**
 * Trả về danh sách row đã patch `available` cho row source='manual' khớp map.
 * Trả về `null` nếu không có gì thay đổi — caller dùng để skip onChange.
 */
export function patchManualAvailable(
    rows: PackagingDraftItem[],
    availableByMaterial: ReadonlyMap<string, number>,
): PackagingDraftItem[] | null {
    if (availableByMaterial.size === 0) return null
    let mutated = false
    const next = rows.map((r) => {
        if (r.source !== 'manual') return r
        const avail = availableByMaterial.get(r.packaging_material_id)
        if (avail === undefined || avail === r.available) return r
        mutated = true
        return { ...r, available: avail }
    })
    return mutated ? next : null
}

/** Dựng 1 dòng vật tư thêm tay.
 *
 *  `rowId` do caller sinh (crypto.randomUUID) — vật tư thêm tay nay là dòng
 *  hàng bình thường nên CÙNG một vật tư có thể xuất hiện nhiều dòng, không thể
 *  định danh bằng (material_id, line_key) như trước. Tham số `current` giữ lại
 *  cho tương lai (vd cảnh báo trùng BOM) chứ không còn dùng để chặn trùng.
 */
export function buildManualRow(
    _current: ReadonlyArray<PackagingDraftItem>,
    mat: PickedPackagingMaterial,
    availableByMaterial: ReadonlyMap<string, number>,
    rowId: string,
): PackagingDraftItem {
    return {
        row_id: rowId,
        packaging_material_id: mat.id,
        material_name: mat.name,
        material_sku: mat.sku,
        material_unit: mat.unit,
        quantity: 1,
        available: availableByMaterial.get(mat.id) ?? 0,
        source: 'manual',
        unit_price: mat.sale_price ?? 0,
        discount_percent: 0,
        product_id: '',
        line_key: '',
    }
}

/**
 * Chuyển kết quả endpoint check-packaging-surcharge thành dòng vật tư có giá.
 * Số lượng do BOM ấn định.
 */
export function buildSurchargeRows(
    surcharge: ReadonlyArray<{
        line_key?: string
        product_id: string
        material_id: string
        material_name: string
        material_unit: string
        unit_price: number
        quantity: number
        available?: number
    }>,
): PackagingDraftItem[] {
    return surcharge.map((s) => ({
        packaging_material_id: s.material_id,
        material_name: s.material_name,
        material_sku: '',
        material_unit: s.material_unit,
        quantity: s.quantity,
        available: s.available ?? 0,
        source: 'bom_autofill' as const,
        unit_price: s.unit_price,
        discount_percent: 0,
        product_id: s.product_id,
        line_key: s.line_key ?? '',
    }))
}

export function mergeSurcharge(
    current: PackagingDraftItem[],
    surcharge: Array<{
        line_key?: string
        product_id: string
        material_id: string
        material_sku?: string
        material_name: string
        material_unit: string
        unit_price: number
        quantity: number
        available?: number
    }>,
): PackagingDraftItem[] {
    // Key theo (line_key, material_id) — line_key = dòng đơn (create: field.id,
    // edit: order_item.id). Cho phép TRÙNG sản phẩm/vật tư trên nhiều dòng: mỗi
    // dòng là 1 nhóm riêng. Fallback product_id khi endpoint không trả line_key.
    const groupKey = (s: { line_key?: string; product_id: string }) =>
        s.line_key || s.product_id
    const surKey = (grp: string, materialId: string) => `${grp}::${materialId}`
    const surMap = new Map(
        surcharge.map((s) => [surKey(groupKey(s), s.material_id), s]),
    )
    const merged: PackagingDraftItem[] = []

    for (const row of current) {
        // Row không gắn dòng (availability/thêm tay) → không do surcharge quản.
        if (!row.line_key) {
            merged.push(row)
            continue
        }
        const k = surKey(row.line_key, row.packaging_material_id)
        const sur = surMap.get(k)
        if (sur) {
            // LUÔN giữ unit_price snapshot (giá user sửa / giá chốt lúc tạo đơn).
            // Trước đây có ngoại lệ refresh giá cho dòng charge_only giá 0; cờ đó
            // đã bỏ nên ngoại lệ không còn đối tượng nào. KHÔNG nới ra cho mọi
            // dòng giá 0: giá 0 ở đây là người bán chủ động đặt, refresh sẽ ghi
            // đè mất.
            merged.push({
                ...row,
                quantity: sur.quantity,
                material_name: sur.material_name,
                material_unit: sur.material_unit,
                available: sur.available ?? row.available,
            })
            surMap.delete(k)
        }
        // else: BOM không còn vật tư có giá này ở dòng đó → bỏ row
    }
    for (const sur of surMap.values()) {
        merged.push({
            packaging_material_id: sur.material_id,
            material_name: sur.material_name,
            material_sku: sur.material_sku ?? '',
            material_unit: sur.material_unit,
            quantity: sur.quantity,
            available: sur.available ?? 0,
            source: 'bom_autofill',
            unit_price: sur.unit_price,
            discount_percent: 0,
            product_id: sur.product_id,
            // Luôn có group key (khớp endpoint: echo product_id khi FE không gửi
            // line_key) để loop merge sau nhận diện đây là row do surcharge quản.
            line_key: sur.line_key || sur.product_id,
        })
    }

    return merged
}

// So sánh shallow: length + (material, qty, available, unit_price).
export function rowsChanged(
    next: PackagingDraftItem[],
    prev: PackagingDraftItem[],
): boolean {
    if (next.length !== prev.length) return true
    return next.some(
        (r, i) =>
            r.packaging_material_id !== prev[i]?.packaging_material_id ||
            r.quantity !== prev[i]?.quantity ||
            r.available !== prev[i]?.available ||
            r.unit_price !== prev[i]?.unit_price ||
            r.discount_percent !== prev[i]?.discount_percent,
    )
}

/** Draft vật tư như backend trả về (GET đơn hàng). */
export interface PackagingDraftFromServer {
    packaging_material_id: string
    material_name?: string | null
    material_sku?: string | null
    material_unit?: string | null
    quantity: number
    source?: string | null
    unit_price?: number | null | null
    discount_percent?: number | null
    product_id?: string | null
    order_item_id?: string | null
    /** Vị trí dòng trên bảng hàng hóa — chung dãy số với sort_order của dòng
     *  sản phẩm (xem `@/lib/order-line-order`). Đơn cũ = 0. */
    sort_order?: number | null
}

/** Chuyển draft đã lưu thành state của form sửa đơn.
 *
 *  Điểm quan trọng: server KHÔNG lưu row_id (nó chỉ sống ở client), nên phải
 *  sinh lại cho mỗi dòng THÊM TAY khi hydrate. Nếu không, hai dòng thêm tay
 *  cùng một vật tư sẽ cho ra cùng khoá "::<material_id>" và sửa/xoá dòng này sẽ
 *  đụng dòng kia. Dòng gắn dòng đơn không cần vì đã duy nhất theo
 *  (order_item_id, vật tư).
 */
export function hydrateDraftItems(
    drafts: ReadonlyArray<PackagingDraftFromServer>,
): PackagingDraftItem[] {
    return drafts.map((d) => {
        const lineKey = d.order_item_id ?? ''
        return {
            ...(lineKey ? {} : { row_id: newMaterialRowId() }),
            packaging_material_id: d.packaging_material_id,
            material_name: d.material_name ?? '',
            material_sku: d.material_sku ?? '',
            material_unit: d.material_unit ?? '',
            quantity: d.quantity,
            // Tồn thật được patch sau khi usePackagingLots trả dữ liệu.
            available: 0,
            source: d.source === 'bom_autofill' ? 'bom_autofill' : 'manual',
            unit_price: d.unit_price ?? 0,
            discount_percent: d.discount_percent ?? 0,
            product_id: d.product_id ?? '',
            line_key: lineKey,
            // Giữ thứ tự dòng đã lưu để form sửa đơn hiện lại đúng thế xen kẽ
            // sản phẩm/vật tư người bán đã nhập (FD #358).
            row_seq: d.sort_order ?? undefined,
        }
    })
}
